import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Form, FormSpy, Field } from 'react-final-form'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import RefreshIcon from '@material-ui/icons/Refresh'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useSnackbar } from 'notistack'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import cookie from 'react-cookies'

import api from 'utils/api'
import { dimensionList } from 'data/options'
import MDateRangePicker from 'components/forms/MDateRangePicker'
import MainLayout from 'layout/MainLayout'
import MAutocomplete from 'components/forms/MAutocomplete'
import MTreeTable from 'components/MTreeTable/MTreeTable'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      minWidth: 250,
    },
    select: {
      minWidth: 110,
    },
    form: {
      width: '100%', // Fix IE 11 issue.
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    button: {
      height: 50,
      verticalAlign: 'middle',
    },
    table: {
      maxHeight: 'calc(100vh - 200px)',
      minHeight: 'calc(100vh - 200px)',
    },
    backDrop: {
      zIndex: 11,
    },
    paper: {
      marginTop: theme.spacing(1),
    },
  }),
)

let defaultFilter = {
  dimension1: 'event_name',
  dimension2: '',
  dimension3: '',
  device: 'All',
  dateRange: {
    startDate: new Date(),
    endDate: new Date(),
  },
}

const columns: any[] = [
  { field: 'dimension1', headerName: 'Event Name', width: 'auto' },
  { field: 'count', headerName: 'Count', align: 'center', width: 100 },
]

type Props = {
  project?: string
}
const GA4Events: React.FC<Props> = ({ project }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [state, setState] = useState<{
    loading: boolean
    rows: any[]
    filter?: any
  }>({
    loading: true,
    rows: [],
    filter: defaultFilter,
  })

  const fetchData = async (params: any) => {
    setState({
      ...state,
      loading: true,
    })

    try {
      cookie.save('date-range', params.dateRange, { path: '/' })
      const res = await api.get('/events', {
        ...params,
        ...params.dateRange,
        project: project,
        dimensionValues: [],
        dateRange: undefined,
      })

      setState({
        loading: false,
        rows: res,
        filter: params,
      })

    } catch {
      setState({
        ...state,
        loading: false,
      })
    }
  }

  const fetchDetailData = async (params: any, parentIds: any[]) => {

    if (
      parentIds.length === 1 &&
      (params.dimension2 === '' || params.dimension2 === null)
    ) {
      enqueueSnackbar('Please select the dimenison')
      return false
    } else if (
      parentIds.length === 2 &&
      (params.dimension3 === '' || params.dimension3 === null)
    ) {
      enqueueSnackbar('Please select the dimenison')
      return false
    }

    setState({
      ...state,
      loading: true,
    })

    try {
      cookie.save('date-range', params.dateRange, { path: '/' })
      let dimensionValues = []
      let childrenRows
      if (state.rows[parentIds[0]].dimension1 === '(Unknown)')
        dimensionValues.push(null)
      else dimensionValues.push(state.rows[parentIds[0]].dimension1)
      childrenRows = state.rows[parentIds[0]]
      for (let i = 1; i < parentIds.length; i++) {
        childrenRows = childrenRows.children[parentIds[i]]
        dimensionValues.push(childrenRows.dimension1)
      }

      const res = await api.get('/events', {
        ...params,
        ...params.dateRange,
        project: project,
        dimensionValues: dimensionValues,
        dateRange: undefined,
      })

      let newRows = state.rows.map((row) => {
        return row
      })
      childrenRows = newRows[parentIds[0]]
      for (let i = 1; i < parentIds.length; i++)
        childrenRows = childrenRows.children[parentIds[i]]

      childrenRows.children = res

      setState({
        loading: false,
        rows: newRows,
        filter: params,
      })
    } catch {
      setState({
        ...state,
        loading: false,
      })
    }
  }

  const onChange = (props: any) => {
    if (
      props.values.dimension2 !== state.filter.dimension2 ||
      props.values.dimension3 !== state.filter.dimension3
    )
      setState({
        ...state,
        filter: props.values,
      })
    else {
      fetchData(props.values)
    }
  }

  const onTreeExpand = (parentIds: any[]) => {
    return fetchDetailData(state.filter, parentIds)
  }
  
  useEffect(() => {
    fetchData(defaultFilter)
  }, []) //eslint-disable-line

  const dateRange = cookie.load('date-range')
  defaultFilter.dateRange = dateRange || defaultFilter.dateRange
  
  return (
    <MainLayout title='GA4 Events Page'>
      <>
        <Form
          initialValues={defaultFilter}
          onSubmit={fetchData}
          render={({ handleSubmit, values }) => (
            <form className={classes.form} onSubmit={handleSubmit} noValidate>
              <FormSpy subscription={{ values: true }} onChange={onChange} />
              <Grid container alignItems='center' spacing={1}>

                <Grid item>
                  <Field
                    className={classes.formControl}
                    name='dimension2'
                    label='Second Dimension'
                    render={(props) => (
                      <MAutocomplete
                        {...props}
                        data={dimensionList}
                        previousData={[state.filter.dimension1]}
                      />
                    )}
                  />
                </Grid>

                <Grid item>
                  <Field
                    className={classes.formControl}
                    name='dimension3'
                    label='Third dimension'
                    render={(props) => (
                      <MAutocomplete
                        {...props}
                        data={dimensionList}
                        previousData={[
                          state.filter.dimension1,
                          state.filter.dimension2,
                        ]}
                      />
                    )}
                  />
                </Grid>

                <Grid item style={{ marginLeft: 'auto' }}>
                  <Button
                    type='submit'
                    variant='contained'
                    color='secondary'
                    className={classes.button}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh Data
                  </Button>
                </Grid>
                <Grid item>
                  <Field
                    label='Date Range'
                    name='dateRange'
                    render={(props) => <MDateRangePicker {...props} />}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        />

        <MTreeTable
          rows={state.rows}
          columns={columns}
          onExpand={onTreeExpand}
          maxDepth={3}
          className={classes.table}
          tableName='event-table'
          showComprehensiveData={true}
        />
        <Backdrop open={state.loading} className={classes.backDrop}>
          <CircularProgress color='inherit' />
        </Backdrop>
      </>
    </MainLayout>
  )
}

const mapStateToProps = (state) => {
  return state.project
}
export default connect(mapStateToProps)(GA4Events)
